/**
 * Banner layer engine — the editable document behind Banner Studio. Layers are
 * authored in a fixed 1600×600 design space (the canonical WINR banner) and
 * rendered by <BannerLayerRender> at any scale, so the editor canvas, every
 * context preview, and the live published surfaces all draw from the exact
 * same data. Publishing persists the LAYERS (banners stay editable forever);
 * `exportBannerImage` flattens to WebP for thumbnails/share cards.
 */

export const BANNER_W = 1600;
export const BANNER_H = 600;

export type BlendMode = 'normal' | 'multiply' | 'screen' | 'overlay' | 'soft-light';

interface LayerBase {
  id: string;
  name: string;
  hidden: boolean;
  locked: boolean;
  /** 0–1 */
  opacity: number;
  blend: BlendMode;
  /** Design-space box (px in 1600×600). Gradient/glass may be full-bleed. */
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface ImageLayer extends LayerBase {
  kind: 'image';
  url: string;
  fit: 'cover' | 'contain';
  radius: number;
}

export interface TextLayer extends LayerBase {
  kind: 'text';
  text: string;
  /** Font size in design px. */
  size: number;
  weight: 400 | 600 | 700 | 800 | 900;
  color: string;
  align: 'left' | 'center' | 'right';
  shadow: boolean;
}

export interface ShapeLayer extends LayerBase {
  kind: 'shape';
  shape: 'rect' | 'ellipse';
  fill: string;
  radius: number;
}

export interface GradientLayer extends LayerBase {
  kind: 'gradient';
  from: string;
  to: string;
  angle: number;
}

export interface GlassLayer extends LayerBase {
  kind: 'glass';
  /** Backdrop blur in design px. */
  blur: number;
  tint: string;
  tintOpacity: number;
  radius: number;
}

export type BannerLayer = ImageLayer | TextLayer | ShapeLayer | GradientLayer | GlassLayer;

export interface BannerDesign {
  w: typeof BANNER_W;
  h: typeof BANNER_H;
  layers: BannerLayer[];
}

/** Editing accessors shared by the canvas / layer panel / inspector — both the
 *  Profile and Community studios supply these from their own stores. */
export interface BannerEditing {
  layers: BannerLayer[];
  selectedId: string | null;
  onSelect: (id: string | null) => void;
  onPatchLayer: (id: string, patch: Partial<BannerLayer>, coalesceKey?: string) => void;
  onSetLayers: (layers: BannerLayer[]) => void;
}

export function emptyBannerDesign(): BannerDesign {
  return { w: BANNER_W, h: BANNER_H, layers: [] };
}

let counter = 0;
function layerId(): string {
  try {
    return crypto.randomUUID();
  } catch {
    counter += 1;
    return `layer-${counter}-${performance.now().toString(36)}`;
  }
}

const BASE = (name: string): LayerBase => ({
  id: layerId(),
  name,
  hidden: false,
  locked: false,
  opacity: 1,
  blend: 'normal',
  x: BANNER_W * 0.3,
  y: BANNER_H * 0.3,
  w: BANNER_W * 0.4,
  h: BANNER_H * 0.4,
});

export function newLayer(kind: BannerLayer['kind'], overrides: Partial<BannerLayer> = {}): BannerLayer {
  switch (kind) {
    case 'image':
      return { ...BASE('Image'), kind, url: '', fit: 'cover', radius: 0, x: 0, y: 0, w: BANNER_W, h: BANNER_H, ...overrides } as ImageLayer;
    case 'text':
      return { ...BASE('Text'), kind, text: 'YOUR TEXT', size: 96, weight: 900, color: '#ffffff', align: 'center', shadow: true, h: 140, w: 900, x: 350, y: 230, ...overrides } as TextLayer;
    case 'shape':
      return { ...BASE('Shape'), kind, shape: 'rect', fill: '#54b9fe', radius: 24, ...overrides } as ShapeLayer;
    case 'gradient':
      return { ...BASE('Gradient'), kind, from: 'rgba(4,4,6,0)', to: 'rgba(4,4,6,0.85)', angle: 180, x: 0, y: 0, w: BANNER_W, h: BANNER_H, ...overrides } as GradientLayer;
    case 'glass':
      return { ...BASE('Glass'), kind, blur: 18, tint: '#ffffff', tintOpacity: 0.08, radius: 28, ...overrides } as GlassLayer;
  }
}

export function duplicateLayer(layer: BannerLayer): BannerLayer {
  return { ...layer, id: layerId(), name: `${layer.name} copy`, x: layer.x + 24, y: layer.y + 24 };
}

const BLENDS: BlendMode[] = ['normal', 'multiply', 'screen', 'overlay', 'soft-light'];
export const BLEND_OPTIONS: { id: BlendMode; label: string }[] = [
  { id: 'normal', label: 'Normal' },
  { id: 'multiply', label: 'Multiply' },
  { id: 'screen', label: 'Screen' },
  { id: 'overlay', label: 'Overlay' },
  { id: 'soft-light', label: 'Soft light' },
];

/** Validate/repair a design read from a stored doc — never trust, never crash. */
export function migrateBannerDesign(raw: unknown): BannerDesign | null {
  if (!raw || typeof raw !== 'object') return null;
  const d = raw as Partial<BannerDesign>;
  if (!Array.isArray(d.layers)) return null;
  const layers: BannerLayer[] = [];
  for (const l of d.layers) {
    if (!l || typeof l !== 'object' || typeof (l as LayerBase).id !== 'string') continue;
    const base = l as LayerBase & { kind?: string };
    const clean: LayerBase = {
      id: base.id,
      name: typeof base.name === 'string' ? base.name : 'Layer',
      hidden: base.hidden === true,
      locked: base.locked === true,
      opacity: clamp(num(base.opacity, 1), 0, 1),
      blend: BLENDS.includes(base.blend as BlendMode) ? (base.blend as BlendMode) : 'normal',
      x: num(base.x, 0),
      y: num(base.y, 0),
      w: Math.max(8, num(base.w, BANNER_W)),
      h: Math.max(8, num(base.h, BANNER_H)),
    };
    const k = base.kind;
    const src = l as unknown as Record<string, unknown>;
    if (k === 'image' && typeof src.url === 'string' && !src.url.startsWith('data:')) {
      layers.push({ ...clean, kind: 'image', url: src.url, fit: src.fit === 'contain' ? 'contain' : 'cover', radius: num(src.radius, 0) });
    } else if (k === 'text') {
      layers.push({
        ...clean,
        kind: 'text',
        text: typeof src.text === 'string' ? src.text.slice(0, 120) : '',
        size: clamp(num(src.size, 96), 12, 400),
        weight: ([400, 600, 700, 800, 900] as const).includes(src.weight as 400) ? (src.weight as TextLayer['weight']) : 900,
        color: str(src.color, '#ffffff'),
        align: src.align === 'left' || src.align === 'right' ? src.align : 'center',
        shadow: src.shadow !== false,
      });
    } else if (k === 'shape') {
      layers.push({ ...clean, kind: 'shape', shape: src.shape === 'ellipse' ? 'ellipse' : 'rect', fill: str(src.fill, '#54b9fe'), radius: num(src.radius, 0) });
    } else if (k === 'gradient') {
      layers.push({ ...clean, kind: 'gradient', from: str(src.from, 'rgba(0,0,0,0)'), to: str(src.to, 'rgba(0,0,0,0.8)'), angle: num(src.angle, 180) });
    } else if (k === 'glass') {
      layers.push({ ...clean, kind: 'glass', blur: clamp(num(src.blur, 18), 0, 80), tint: str(src.tint, '#ffffff'), tintOpacity: clamp(num(src.tintOpacity, 0.08), 0, 1), radius: num(src.radius, 0) });
    }
  }
  return { w: BANNER_W, h: BANNER_H, layers };
}

function num(v: unknown, d: number): number {
  return typeof v === 'number' && Number.isFinite(v) ? v : d;
}
function str(v: unknown, d: string): string {
  return typeof v === 'string' && v ? v : d;
}
function clamp(v: number, lo: number, hi: number): number {
  return Math.min(hi, Math.max(lo, v));
}

/**
 * Flatten a design to a WebP blob (thumbnails / share cards). Glass layers
 * export as their tint only — real backdrop blur exists on live surfaces,
 * which render layers directly.
 */
export async function exportBannerImage(design: BannerDesign, scale = 1): Promise<Blob> {
  const canvas = document.createElement('canvas');
  canvas.width = BANNER_W * scale;
  canvas.height = BANNER_H * scale;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas unavailable');
  ctx.scale(scale, scale);

  for (const layer of design.layers) {
    if (layer.hidden) continue;
    ctx.save();
    ctx.globalAlpha = layer.opacity;
    ctx.globalCompositeOperation = layer.blend === 'normal' ? 'source-over' : layer.blend;
    roundedClip(ctx, layer);
    if (layer.kind === 'image' && layer.url) {
      try {
        const img = await loadImage(layer.url);
        drawCover(ctx, img, layer);
      } catch {
        /* missing image — skip layer */
      }
    } else if (layer.kind === 'shape') {
      ctx.fillStyle = layer.fill;
      ctx.fill();
    } else if (layer.kind === 'gradient') {
      const rad = ((layer.angle - 90) * Math.PI) / 180;
      const cx = layer.x + layer.w / 2;
      const cy = layer.y + layer.h / 2;
      const len = (Math.abs(layer.w * Math.cos(rad)) + Math.abs(layer.h * Math.sin(rad))) / 2;
      const g = ctx.createLinearGradient(cx - Math.cos(rad) * len, cy - Math.sin(rad) * len, cx + Math.cos(rad) * len, cy + Math.sin(rad) * len);
      g.addColorStop(0, layer.from);
      g.addColorStop(1, layer.to);
      ctx.fillStyle = g;
      ctx.fill();
    } else if (layer.kind === 'glass') {
      ctx.fillStyle = layer.tint;
      ctx.globalAlpha = layer.opacity * layer.tintOpacity;
      ctx.fill();
    } else if (layer.kind === 'text') {
      ctx.fillStyle = layer.color;
      ctx.font = `${layer.weight} ${layer.size}px Inter, system-ui, sans-serif`;
      ctx.textBaseline = 'middle';
      ctx.textAlign = layer.align;
      if (layer.shadow) {
        ctx.shadowColor = 'rgba(0,0,0,0.55)';
        ctx.shadowBlur = 24;
        ctx.shadowOffsetY = 4;
      }
      const tx = layer.align === 'left' ? layer.x : layer.align === 'right' ? layer.x + layer.w : layer.x + layer.w / 2;
      ctx.fillText(layer.text, tx, layer.y + layer.h / 2, layer.w);
    }
    ctx.restore();
  }

  return await new Promise<Blob>((resolve, reject) =>
    canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('Banner export failed'))), 'image/webp', 0.85),
  );
}

function roundedClip(ctx: CanvasRenderingContext2D, layer: BannerLayer): void {
  const r = 'radius' in layer ? Math.min(layer.radius, layer.w / 2, layer.h / 2) : 0;
  ctx.beginPath();
  if (layer.kind === 'shape' && layer.shape === 'ellipse') {
    ctx.ellipse(layer.x + layer.w / 2, layer.y + layer.h / 2, layer.w / 2, layer.h / 2, 0, 0, Math.PI * 2);
  } else if (r > 0) {
    ctx.roundRect(layer.x, layer.y, layer.w, layer.h, r);
  } else {
    ctx.rect(layer.x, layer.y, layer.w, layer.h);
  }
  ctx.clip();
}

function drawCover(ctx: CanvasRenderingContext2D, img: HTMLImageElement, layer: ImageLayer): void {
  const scale =
    layer.fit === 'contain'
      ? Math.min(layer.w / img.width, layer.h / img.height)
      : Math.max(layer.w / img.width, layer.h / img.height);
  const dw = img.width * scale;
  const dh = img.height * scale;
  ctx.drawImage(img, layer.x + (layer.w - dw) / 2, layer.y + (layer.h - dh) / 2, dw, dh);
}

function loadImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(`Could not load ${url}`));
    img.src = url;
  });
}
